import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useMap } from '@vis.gl/react-google-maps';
import React, { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { config } from '@/stitches.config';
import { IDealer } from '@/types/dealer/dealer';

const createMarkerContent = (isSelect?: boolean) => {
  const container = document.createElement('div');
  const img = document.createElement('img');

  container.style.cssText = 'width: 36px; height: 50px;';
  img.style.cssText = 'width: 100%; height: 100%;';
  img.src = isSelect
    ? '/imgs/dealer/marker-selected.svg'
    : '/imgs/dealer/marker.svg';

  container.appendChild(img);
  return container;
};

const createClusterMarkerContent = (count: number) => {
  const container = document.createElement('div');
  container.className = 'marker-container';
  container.style.display = 'flex';
  container.style.justifyContent = 'center';
  container.style.alignItems = 'center';
  container.style.width = '36px';
  container.style.height = '50px';
  container.style.fontFamily = 'var(--fonts-eurothai)';
  container.style.color = 'var(--colors-primary)';
  container.style.fontSize = '14px';
  container.style.fontWeight = 'normal';

  const img = document.createElement('img');
  img.src = '/imgs/dealer/marker-cluster.svg';
  img.style.position = 'absolute';
  img.style.inset = '0';

  const span = document.createElement('span');
  span.style.position = 'relative';
  span.style.top = '-8px';
  span.textContent = String(count);

  container.appendChild(img);
  container.appendChild(span);
  return container;
};

export type ClusteredMarkersProps = {
  dealers?: IDealer[];
  selectedDealer?: IDealer;
  onClickMarker?: (id: string) => void;
};

export const ClusteredMarkers = ({
  dealers = [],
  selectedDealer,
  onClickMarker,
}: ClusteredMarkersProps) => {
  const isMaxLg = useMediaQuery(config.media.maxlg);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const map = useMap();
  const [isLoadAdvancedMarkerElement, setIsLoadAdvancedMarkerElement] =
    useState<boolean>(false);

  useEffect(() => {
    const loadMarkerLibrary = async () => {
      await google.maps.importLibrary('marker');
      setIsLoadAdvancedMarkerElement(true);
    };

    loadMarkerLibrary();
  }, []);

  useEffect(() => {
    if (!map) return;
    if (!isLoadAdvancedMarkerElement) return;

    // clear
    for (const marker of markersRef.current) {
      google.maps.event.clearInstanceListeners(marker);
    }
    if (clustererRef.current) {
      google.maps.event;
      clustererRef.current.setMap(null);
    }

    // create markers
    const markers = dealers.map((dealer) => {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: {
          lat: Number(dealer?.sale?.lat) || 0,
          lng: Number(dealer?.sale?.lon) || 0,
        },
        content: createMarkerContent(),
      });

      marker?.setAttribute('uid', dealer.uid);
      marker.addListener('click', () => {
        onClickMarker && onClickMarker(dealer.uid);
      });

      return marker;
    });
    markersRef.current = markers;

    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      renderer: {
        render: ({ count, position }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: createClusterMarkerContent(count),
          });
        },
      },
    });

    // set bound
    if (selectedDealer) {
      map.setCenter({
        lat: Number(selectedDealer?.sale?.lat) || 0,
        lng: Number(selectedDealer?.sale?.lon) || 0,
      });
      map.setZoom(14);
    } else {
      const bounds = new google.maps.LatLngBounds();
      for (const marker of markers) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        bounds.extend(marker.position);
      }
      map.fitBounds(bounds);
    }

    return () => {
      for (const marker of markersRef.current) {
        google.maps.event.clearInstanceListeners(marker);
      }
      if (clustererRef.current) {
        google.maps.event;
        clustererRef.current.setMap(null);
      }
    };
  }, [
    map,
    dealers,
    isLoadAdvancedMarkerElement,
    onClickMarker,
    selectedDealer,
  ]);

  useEffect(() => {
    if (!selectedDealer) return;
    if (!markersRef) return;
    if (isMaxLg) return;

    for (const marker of markersRef.current) {
      if (marker.getAttribute('uid') === selectedDealer.uid) {
        marker.content = createMarkerContent(true);
      }
    }
  }, [selectedDealer, isMaxLg]);

  return <></>;
};
